import { FetchResult } from 'src/types/TApiQueries';
import {
  ShareTemplate,
  ShareTemplateResponse,
  ShareTemplatesResponse,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  PresetListResponse,
  AddPresetsResponse,
} from 'src/types/TShare';
import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from '../utils/helpers';

/**
 * List the authenticated user's templates, one page at a time
 * GET /api/v1/sharetemplates?limit=&offset=
 *
 * The server pages (default 50, max 100) and reports `total`; without
 * `page` this returns the first page only.
 */
export async function listTemplates(
  accessToken: string,
  page?: { limit?: number; offset?: number },
): Promise<FetchResult<ShareTemplatesResponse>> {
  const params = new URLSearchParams();
  if (page?.limit !== undefined) params.set('limit', String(page.limit));
  if (page?.offset !== undefined) params.set('offset', String(page.offset));
  const query = params.toString();
  const url = `${config.hostUrl}/api/v1/sharetemplates${query ? `?${query}` : ''}`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/**
 * Get a specific template by ID
 * GET /api/v1/sharetemplates/{templateId}
 */
export async function getTemplate(
  accessToken: string,
  templateId: string,
): Promise<FetchResult<ShareTemplateResponse>> {
  const url = `${config.hostUrl}/api/v1/sharetemplates/${templateId}`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/**
 * Create a new template
 * PUT /api/v1/sharetemplate
 */
export async function createTemplate(
  accessToken: string,
  data: CreateTemplateRequest,
): Promise<FetchResult<ShareTemplateResponse>> {
  const url = `${config.hostUrl}/api/v1/sharetemplate`;

  return performFetchWithLogging(url, {
    method: 'PUT',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing template
 * PATCH /api/v1/sharetemplates/{templateId}
 */
export async function updateTemplate(
  accessToken: string,
  templateId: string,
  data: UpdateTemplateRequest,
): Promise<FetchResult<ShareTemplateResponse>> {
  const url = `${config.hostUrl}/api/v1/sharetemplates/${templateId}`;

  return performFetchWithLogging(url, {
    method: 'PATCH',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a template
 * DELETE /api/v1/sharetemplates/{templateId}
 */
export async function deleteTemplate(
  accessToken: string,
  templateId: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/sharetemplates/${templateId}`;

  return performFetchWithLogging(url, {
    method: 'DELETE',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/**
 * Set a template as the user's default
 * POST /api/v1/sharetemplates/{templateId}/default
 */
export async function setDefaultTemplate(
  accessToken: string,
  templateId: string,
): Promise<FetchResult<ShareTemplateResponse>> {
  const url = `${config.hostUrl}/api/v1/sharetemplates/${templateId}/default`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({}),
  });
}

/**
 * Get the user's default template
 * GET /api/v1/sharetemplates/default
 */
export async function getDefaultTemplate(
  accessToken: string,
): Promise<FetchResult<ShareTemplateResponse>> {
  const url = `${config.hostUrl}/api/v1/sharetemplates/default`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/**
 * Get available preset templates catalog
 * GET /api/v1/sharetemplates/presets
 */
export async function getPresetTemplates(
  accessToken: string,
): Promise<FetchResult<PresetListResponse>> {
  const url = `${config.hostUrl}/api/v1/sharetemplates/presets`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/**
 * Add preset templates to user's account
 * POST /api/v1/sharetemplates/presets
 */
export async function addPresetTemplates(
  accessToken: string,
  presetIds: string[],
): Promise<FetchResult<AddPresetsResponse>> {
  const url = `${config.hostUrl}/api/v1/sharetemplates/presets`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ preset_ids: presetIds }),
  });
}

